import { CUSTOMER_ACCOUNTS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter, useRoleAuth } from '@src/hooks';
import { useCustomers } from '@src/hooks/useCustomer';
import type { CustomerMarketAccount } from '@talos/kyoko';
import { ACTION, CUSTOMER_MARKET_ACCOUNT, EntityAdmin, useMarketAccountStatusesFilter } from '@talos/kyoko';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { CUSTOMER_ACCOUNTS_COLUMNS, getCustomerAccountsDrawerOptions } from './utils';

const WS_REQUEST = { name: CUSTOMER_MARKET_ACCOUNT, IncludePricingRuleMode: true, tag: 'CustomerAccounts' };

export function CustomerAccounts() {
  const { isAuthorized } = useRoleAuth();
  const customers = useCustomers();

  const drawerOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }
    return getCustomerAccountsDrawerOptions({ customers });
  }, [customers]);

  const statusFilter = useMarketAccountStatusesFilter({ label: 'Status' });
  const counterpartiesFilter = useCounterpartiesFilter({ label: 'Customer', nullUntilLoaded: true });
  const availableFilterableProperties = useMemo(() => {
    const properties = [counterpartiesFilter, statusFilter];
    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [counterpartiesFilter, statusFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_ACCOUNTS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  return (
    <EntityAdmin<CustomerMarketAccount>
      title="Customer Accounts"
      subtitle="View and manage market accounts for customer counterparties."
      entityName="Customer Account"
      path="/customer-accounts"
      wsRequest={WS_REQUEST}
      entityIDField="MarketAccountID"
      columns={CUSTOMER_ACCOUNTS_COLUMNS}
      allowAddEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowEditEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowDeleteEntity={isAuthorized(ACTION.EDIT_DEALER)}
      persistKey="dealer/customer-accounts"
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      drawerOptions={drawerOptions}
    />
  );
}
